import React, {useCallback, useEffect, useMemo} from 'react';

import {useLocation, useNavigate} from 'react-router';

import {featureFlags} from '@/config/featureFlags';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';

import {COnboardingTypeStates} from '../main/onboarding/constants/OnboardingConstants';

import {RolesAccount} from './constants';

export const ModakAuthentication = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {pathname} = useLocation();

  const {profile} = useUsersRepository();

  const navigate = useNavigate();

  const isEnabledRequestPhysicalCard =
    featureFlags.isEnabledRequestPhysicalCard;

  const isUserGuest = useMemo(
    () =>
      !profile || (Array.isArray(profile?.role) && profile?.role?.length === 0),
    [profile],
  );

  const redirectGuest = useCallback(() => {
    if (
      !pathname.includes('/authentication') &&
      !pathname.includes('/payment-link') &&
      !pathname.includes('/split-bills') &&
      !pathname.includes('/splits') &&
      !pathname.startsWith('/mobot') &&
      !pathname.startsWith('/golink') &&
      !pathname.includes('/groups') &&
      !pathname.startsWith('/edit-info')
    ) {
      setTimeout(() => navigate('/authentication/sign-up'), 0);
    }
  }, [navigate, pathname]);

  const previousScreen = useMemo(() => {
    const pathnameParts = pathname.split('/').filter(Boolean);
    return pathnameParts[pathnameParts.length - 1] ?? '';
  }, [pathname]);

  const handleRoleNavigation = useCallback(() => {
    if (pathname.includes('/payment-link')) return;
    if (pathname.includes('/split-bills')) return;
    if (pathname.includes('/splits')) return;
    if (pathname.includes('/co-parent-invite')) return;
    if (pathname.includes('/password')) return;
    if (pathname.startsWith('/mobot')) return;
    if (pathname.startsWith('/golink')) return;
    if (pathname.includes('/groups')) return;

    if (profile?.role === RolesAccount.PARENT) {
      if (profile?.onboarding === COnboardingTypeStates.COMPLETED) {
        if (isEnabledRequestPhysicalCard) {
          if (pathname.includes('/onboarding/request-physical-card')) return;
          if (pathname.includes('/onboarding/virtual-card-ready')) return;
        }
        if (pathname.includes('/onboarding/download-app')) return;
        if (pathname.includes('/onboarding/notify-me')) return;
        if (pathname.includes('/onboarding/your-info-is-in-review')) return;
        if (pathname.includes('/parent')) {
          setTimeout(() => navigate(pathname), 0);
        } else {
          setTimeout(
            () =>
              navigate(
                `/onboarding/download-app?previousScreen=${encodeURIComponent(previousScreen)}`,
              ),
            0,
          );
        }
      } else {
        if (pathname.includes('/onboarding/kyc')) {
          navigate('/onboarding/kyc');
        } else {
          navigate('/onboarding');
        }
      }
    }
    if (profile?.role === RolesAccount.TEEN) {
      if (pathname.includes('/teen')) {
        setTimeout(() => navigate(pathname), 0);
      } else {
        setTimeout(() => navigate('/teen/redirection'), 0);
      }
    }
  }, [
    isEnabledRequestPhysicalCard,
    navigate,
    pathname,
    previousScreen,
    profile?.onboarding,
    profile?.role,
  ]);

  useEffect(() => {
    if (isUserGuest) {
      redirectGuest();
    } else {
      handleRoleNavigation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, navigate]);

  return <>{children}</>;
};
